import { Component, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from '@stencil/core';
import { Filter } from '../../03_organisms/lilu-product-list/filter';
import { FilterChangedEvent } from '../../03_organisms/lilu-product-list/filter-changed-event';
import { Color } from './color';
import axios from '../../../utils/get-axios';
import { ActivatedFilter } from '../../02_molecules/lilu-activated-filters/actived-filter';

const ACC = (window as any).ACC;
@Component({
  tag: 'lilu-color-picker',
  styleUrl: 'lilu-color-picker.scss',
  shadow: true,
})
export class LiluColorPicker implements Filter {
  @Prop() colors: string;
  @Prop() value: string;
  @Prop() productUrl: string;
  @Prop() productId: string;
  @Prop() variantSwitch: boolean;
  @Prop() multiSelect = false;
  @Prop() size = 'size-sm';
  @Prop() label = '';
  @Prop() selectedColor = '';
  @State() shownColorName = '';
  @Prop({ mutable: true }) limit = -1;
  @Prop() dataControlFilter: string;
  @State() internalColors: Color[] = [];
  @Prop() ajaxUrl?: string;

  componentWillLoad() {
    this.parseColors();
    this.limit = this.limit === -1 ? this.internalColors.length : this.limit;
  }

  @Watch('colors')
  parseColors() {
    if (this.colors !== undefined) {
      this.internalColors = JSON.parse(this.colors);
    }
  }

  @Event()
  filterChanged: EventEmitter<FilterChangedEvent>;

  onColorChange(index: number) {
    if (this.multiSelect) {
      this.internalColors[index].selected = !this.internalColors[index].selected;
    } else {
      // reset selected color and set select by index
      this.internalColors.forEach((color: Color) => (color.selected = false));
      this.internalColors[index].selected = true;

      if (this.internalColors[index].colorName) {
        this.selectedColor = this.internalColors[index].colorName;
      }
    }

    this.internalColors = [...this.internalColors]; // Update state

    if (this.variantSwitch) {
      // trigger variant switch for product tile
      this.switchVariant(this.internalColors[index].productNumber);
    } else {
      // Trigger filter event for changed data
      const filterChangeEvent: FilterChangedEvent = {
        filterControlName: this.dataControlFilter,
        label: this.internalColors[index].label,
        value: this.internalColors[index].value,
        multiSelect: this.multiSelect,
      };
      this.filterChanged.emit(filterChangeEvent);
    }
  }

  @Method()
  async getActiveFilterByValue(filterValue: string): Promise<ActivatedFilter> {
    return new Promise((resolve, _) => {
      const color = this.internalColors.find(color => color.value === filterValue);
      if (!color) {
        resolve(null);
      }

      const activatedFilter: ActivatedFilter = {
        filterControlName: this.dataControlFilter,
        label: color.label,
        value: color.value,
        multiSelect: this.multiSelect,
      };
      resolve(activatedFilter);
    });
  }

  @Method()
  async deactivateFilter(filterValue: string) {
    this.internalColors.forEach(color => {
      if (color.value === filterValue) color.selected = false;
    });
    this.internalColors = [...this.internalColors]; // Update state
  }

  @Method()
  async resetAllFilters() {
    this.internalColors.forEach(color => (color.selected = false));
    this.internalColors = [...this.internalColors]; // Update state
  }

  @Method()
  async switchVariant(productId) {
    axios
      .post(
        `${this.ajaxUrl}/${productId}`,
        {},
        {
          headers: {
            CSRFToken: ACC.config.CSRFToken,
          },
        },
      )
      .then(response => {
        const currentTile = document.querySelector(`lilu-product-tile[data-product-id="${this.productId}"]`);
        const newTile = this.htmlToElement(response.data);
        currentTile.parentNode.replaceChild(newTile, currentTile);
      });
  }

  /**
   * Generate a virtual DOM element from a HTML String an returns HTML Node
   * @param html string
   * @returns HTML Node
   */
  htmlToElement(html) {
    const template = document.createElement('template');
    html = html.trim(); // Never return a text node of whitespace as the result
    template.innerHTML = html;
    return template.content.firstChild;
  }

  getColorBubble(option) {
    const colorCodes = option.colorCode;
    if (colorCodes.length > 1) {
      let colorParts = '';
      colorCodes.forEach(color => {
        colorParts += `<li style="--color: ${color}; --width: ${100 / colorCodes.length}%"></li>`;
      });
      return <ul class="multi" innerHTML={colorParts} onMouseEnter={() => this.onColorHover(option.colorName)} onMouseLeave={() => this.onColorHoverExit()}></ul>;
    } else {
      if (colorCodes[0] === 'multi') {
        return <div class="circle multi-rainbow " onMouseEnter={() => this.onColorHover(option.colorName)} onMouseLeave={() => this.onColorHoverExit()}></div>;
      } else {
        return (
          <div
            class={'circle ' + this.calculateTextColor(colorCodes[0])}
            style={{ '--color': colorCodes[0] }}
            onMouseEnter={() => this.onColorHover(option.colorName)}
            onMouseLeave={() => this.onColorHoverExit()}
          ></div>
        );
      }
    }
  }

  onColorHover(hoveredColorName) {
    this.shownColorName = hoveredColorName;
  }

  onColorHoverExit() {
    this.shownColorName = this.selectedColor;
  }

  calculateTextColor(bubbleColor) {
    const color = bubbleColor.charAt(0) === '#' ? bubbleColor.substring(1, 7) : bubbleColor;
    const r = parseInt(color.substring(0, 2), 16); // hexToR
    const g = parseInt(color.substring(2, 4), 16); // hexToG
    const b = parseInt(color.substring(4, 6), 16); // hexToB
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? 'darkColor' : '';
  }

  render() {
    const circles = [];

    for (let i = 0; i < this.limit && i < this.internalColors.length; i++) {
      if (this.internalColors[i].selected && this.shownColorName === '') {
        this.selectedColor = this.internalColors[i].colorName;
        this.shownColorName = this.selectedColor;
      }
      if (i < this.internalColors.length) {
        if (this.internalColors[i].productUrl) {
          circles.push(
            <a href={this.internalColors[i].productUrl} target="_self" class={this.internalColors[i].selected ? 'checked' : ''}>
              {this.getColorBubble(this.internalColors[i])}
            </a>,
          );
        } else {
          circles.push(
            <label htmlFor={`${i}`} title={this.internalColors[i].label}>
              <input
                class="color-input"
                name="colors"
                id={`${i}`}
                type={this.multiSelect ? 'checkbox' : 'radio'}
                style={{ '--color': this.internalColors[i].colorCode }}
                onClick={this.onColorChange.bind(this, i)}
                checked={this.internalColors[i].selected}
                value={this.internalColors[i].productNumber}
                data-colorname={this.internalColors[i].colorName}
              ></input>
              {this.getColorBubble(this.internalColors[i])}
            </label>,
          );
        }
      }
    }

    return (
      <Host>
        {this.label ? (
          <lilu-p color="nero" size="size-16 lg-size-18" font="semibold">
            {this.label}
          </lilu-p>
        ) : (
          ''
        )}
        {this.shownColorName ? (
          <lilu-p color="nero" size="size-16 lg-size-16" font="light">
            {this.shownColorName}
          </lilu-p>
        ) : (
          ''
        )}
        <div class={`circles ${this.size}`}>
          {circles}
          {this.internalColors.length > this.limit ? <span class="additional-colors"> +{this.internalColors.length - this.limit} </span> : ''}
        </div>
      </Host>
    );
  }
}
